package org.multiplatform.kotfolio.pages.compose.side_effect

import androidx.compose.runtime.Composable
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ul

@Page("/compose/side_effect/saver_custom")
@Composable
fun SaverCustomPage() {
    BlogLayout(
        PageProperties(
            "Saver Custom",
            ""
        ),
        BlogProperties("Jetpack Compose", "Saver Custom"))
    {
        Column(
            horizontalAlignment = Alignment.Start
        ) {

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "For custom or complex data types, Compose allows you to define how the data should be saved and restored using a custom Saver.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "rememberSaveable works hand-in-hand with the SavedStateRegistry to automatically save and restore state. By default, it saves the entire object using Parcelable. But what if your model has more complex properties, and you want to optimize what gets saved? That’s where a custom Saver comes in.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A custom Saver is used when you want to save and restore state objects that aren’t automatically handled by rememberSaveable (like complex data classes, custom objects, or non-primitive types). The Saver describes how the object of Original class can be simplified and converted into something which is Saveable.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            //https://gist.github.com/benigumocom
            KotlinCode(
                code = $$"""
                    //Navigation3
                    
                    @Serializable
                    sealed interface TabRoot : NavKey {
                      val label: String
                      val selectedIcon: ImageVector
                      val unselectedIcon: ImageVector

                      companion object {
                        val entries = listOf(Home, Search, Profile)
                      }
                    }

                    @Serializable
                    data object Home : TabRoot {
                      override val label = "Home"
                      override val selectedIcon = Icons.Filled.Home
                      override val unselectedIcon = Icons.Outlined.Home
                    }

                    @Serializable
                    data object Search : TabRoot {
                      override val label = "Search"
                      override val selectedIcon = Icons.Filled.Search
                      override val unselectedIcon = Icons.Outlined.Search
                    }

                    @Serializable
                    data object Profile : TabRoot {
                      override val label = "Profile"
                      override val selectedIcon = Icons.Filled.TagFaces
                      override val unselectedIcon = Icons.Outlined.TagFaces
                    }

                    @Serializable
                    data class Result(val keyword: String) : NavKey

                    @Serializable
                    data class Detail(val id: String) : NavKey


                    @OptIn(ExperimentalMaterial3Api::class)
                    @Composable
                    fun MainScreen() {

                      var currentTab by rememberSerializable { mutableStateOf<TabRoot>(Home) }

                      // Map recreation is cheap; NavBackStack state persists.
                      val stacks = TabRoot.entries.associateWith { root ->
                        rememberNavBackStack(root)
                      }

                      // custom server approach to save the map of NavBackStacks
                      //val stacks = rememberBackStacks(TabRoot.entries)

                      // This re-evaluates automatically on currentTab changes.
                      val currentStack = stacks[currentTab]!!

                      Scaffold(
                        topBar = {
                          TopAppBar(
                            colors = TopAppBarDefaults.topAppBarColors(
                              containerColor = MaterialTheme.colorScheme.primaryContainer,
                              titleContentColor = MaterialTheme.colorScheme.primary,
                            ),
                            title = { Text(stringResource(R.string.app_name)) },
                          )
                        },
                        bottomBar = {
                          NavigationBar {
                            TabRoot.entries.forEach { tabRoot ->

                              val selected = currentStack.first() == tabRoot

                              NavigationBarItem(
                                selected = selected,
                                onClick = {

                                  // If the tab is selected, reset its stack to the root;
                                  // otherwise, navigate to that stack.
                                  if (selected) {
                                    currentStack.apply {
                                      clear()
                                      add(tabRoot)
                                    }
                                  } else {
                                    currentTab = tabRoot
                                  }

                                  log(currentTab, stacks)
                                },
                                icon = {
                                  Icon(
                                    imageVector = if (selected) tabRoot.selectedIcon else tabRoot.unselectedIcon,
                                    contentDescription = tabRoot.label
                                  )
                                },
                                label = { Text(tabRoot.label) },
                                alwaysShowLabel = true
                              )
                            }
                          }

                        }
                      ) { innerPadding ->
                        NavDisplay(
                          backStack = currentStack,
                          modifier = Modifier.padding(innerPadding),
                          onBack = {

                            // If at the root of a stack, exit the app;
                            // otherwise, pop the back stack.
                            currentStack.removeAt(currentStack.lastIndex)

                            log(currentTab, stacks)
                          },
                          entryProvider = entryProvider {
                            entry<Home> { HomeScreen() }
                            entry<Search> {
                              SearchScreen(
                                onSearch = { keyword: String ->
                                  currentStack.add(Result(keyword))

                                  log(currentTab, stacks)
                                }
                              )
                            }
                            entry<Result> { result ->
                              ResultScreen(
                                keyword = result.keyword,
                                onClick = { id: String ->
                                  currentStack.add(Detail(id))

                                  log(currentTab, stacks)
                                }
                              )
                            }
                            entry<Detail> { detail -> DetailScreen(detail.id) }
                            entry<Profile> { ProfileScreen() }
                          }
                        )
                      }
                    }


                    @Composable
                    fun HomeScreen() {
                      Box(
                        modifier = Modifier.fillMaxSize(),
                        contentAlignment = Alignment.Center
                      ) {
                        Text("Home")
                      }
                    }

                    @Composable
                    fun SearchScreen(
                      onSearch: (String) -> Unit
                    ) {
                      var text by rememberSaveable { mutableStateOf("") }

                      Column(
                        modifier = Modifier.fillMaxSize(),
                        verticalArrangement = Arrangement.Center,
                        horizontalAlignment = Alignment.CenterHorizontally
                      ) {
                        Text("Search")
                        TextField(
                          value = text,
                          onValueChange = { new -> text = new },
                          singleLine = true,
                          keyboardOptions = KeyboardOptions(imeAction = ImeAction.Done),
                          keyboardActions = KeyboardActions(onDone = { onSearch(text) })
                        )
                      }
                    }

                    @Composable
                    fun ProfileScreen() {
                      Box(
                        modifier = Modifier.fillMaxSize(),
                        contentAlignment = Alignment.Center
                      ) {
                        Text("Profile")
                      }
                    }

                    @Composable
                    fun ResultScreen(
                      keyword: String,
                      onClick: (String) -> Unit
                    ) {
                      Column(
                        modifier = Modifier.fillMaxSize(),
                        verticalArrangement = Arrangement.Center,
                        horizontalAlignment = Alignment.CenterHorizontally
                      ) {
                        Text("Search Result")
                        repeat(3) {
                          Text(
                            text = "- ${keyword.reversed()}",
                            modifier = Modifier.clickable {
                              onClick(keyword.reversed())
                            }
                          )
                        }
                      }
                    }

                    @Composable
                    fun DetailScreen(id: String) {
                      Column(
                        modifier = Modifier.fillMaxSize(),
                        verticalArrangement = Arrangement.Center,
                        horizontalAlignment = Alignment.CenterHorizontally
                      ) {
                        Text("Detail")
                        repeat(5) {
                          Text(id.repeat(3))
                        }
                      }
                    }

                    fun log(
                      currentTabRoot: TabRoot,
                      stacks: Map<TabRoot, NavBackStack<NavKey>>
                    ) {
                      Timber.d("current tab: $currentTabRoot")
                      Timber.d("map: ${System.identityHashCode(stacks)}")
                      stacks.forEach { (tab, stack) ->
                        Timber.d("stack ${System.identityHashCode(stack)} for $tab: ${stack.toList()}")
                      }
                      Timber.d("---")
                    }

                    @Composable
                    fun <TabRoot : NavKey> rememberBackStacks(
                      tabRoots: List<TabRoot>
                    ): SnapshotStateMap<TabRoot, NavBackStack<NavKey>> {
                      return rememberSaveable(
                        inputs = arrayOf(tabRoots), // dynamic creation of tabs
                        saver = Saver<SnapshotStateMap<TabRoot, NavBackStack<NavKey>>, List<Pair<TabRoot, NavBackStack<NavKey>>>>(
                          save = { map ->
                            map.toList()
                            // [
                            //   (Home, androidx.navigation3.runtime.NavBackStack@c7794ba),
                            //   (Search, androidx.navigation3.runtime.NavBackStack@172f6b),
                            //   (Profile, androidx.navigation3.runtime.NavBackStack@213fac8)
                            // ]
                          },
                          restore = { restored ->
                            mutableStateMapOf<TabRoot, NavBackStack<NavKey>>().apply {
                              putAll(restored)
                            }
                            // SnapshotStateMap(
                            //   value={
                            //     Home=androidx.navigation3.runtime.NavBackStack@8845886,
                            //     Search=androidx.navigation3.runtime.NavBackStack@f6b547,
                            //     Profile=androidx.navigation3.runtime.NavBackStack@310174
                            //   }
                            // )@265869092
                          }
                        )
                      ) {
                        mutableStateMapOf<TabRoot, NavBackStack<NavKey>>().apply {
                          tabRoots.forEach { key ->
                            this[key] = NavBackStack(key)
                          }
                        }
                      }
                    }
                    """.trimIndent()
            )

            //https://medium.com/compose-d/building-component-with-state-holder-pattern-in-jetpack-compose-089e94ce27f7
            KotlinCode(
                code = """
                    @Stable
                    class MyStepperState(
                        val initialValue: Int,
                        val minValue: Int = 0,
                        val maxValue: Int = 10
                    ) {
                        // The current value, making the setter private so it can only be changed via defined actions
                        var value by mutableIntStateOf(initialValue)
                            private set

                        // Derived state to automatically determine if buttons should be enabled
                        val canDecrement: Boolean get() = value > minValue
                        val canIncrement: Boolean get() = value < maxValue

                        // Encapsulated UI logic
                        fun increment() {
                            if (canIncrement) value++
                        }

                        fun decrement() {
                            if (canDecrement) value--
                        }

                        companion object {
                            private const val KEY_VALUE = "value"
                            private const val KEY_MIN = "min"
                            private const val KEY_MAX = "max"

                            // The custom Saver object
                            val Saver = mapSaver(
                                // 1. Save into a Map using keys
                                save = { state ->
                                    mapOf(
                                        KEY_VALUE to state.value,
                                        KEY_MIN to state.minValue,
                                        KEY_MAX to state.maxValue
                                    )
                                },
                                // 2. Restore from the Map using the same keys
                                restore = { savedMap ->
                                    MyStepperState(
                                        initialValue = savedMap[KEY_VALUE] as Int,
                                        minValue = savedMap[KEY_MIN] as Int,
                                        maxValue = savedMap[KEY_MAX] as Int
                                    )
                                }
                            )
                        }
                    }

                    @Composable
                    fun MyStepper(
                        modifier: Modifier = Modifier,
                        state: MyStepperState = rememberMyStepperState()
                    ) {
                        Row(
                            modifier = modifier
                                .clip(RoundedCornerShape(8.dp)),
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.SpaceBetween
                        ) {
                            // Decrement Button
                            IconButton(
                                onClick = { state.decrement() },
                                enabled = state.canDecrement
                            ) {
                                Icon(
                                    imageVector = Icons.Default.Remove,
                                    contentDescription = "Decrease quantity"
                                )
                            }

                            // Current Value
                            Text(
                                text = state.value.toString(),
                                style = MaterialTheme.typography.titleMedium,
                                textAlign = TextAlign.Center,
                                modifier = Modifier
                                    .padding(horizontal = 8.dp)
                                    .widthIn(min = 28.dp)
                            )

                            // Increment Button
                            IconButton(
                                onClick = { state.increment() },
                                enabled = state.canIncrement
                            ) {
                                Icon(
                                    imageVector = Icons.Default.Add,
                                    contentDescription = "Increase quantity"
                                )
                            }
                        }
                    }

                    @Composable
                    fun rememberMyStepperState(
                        initialValue: Int = 0,
                        minValue: Int = 0,
                        maxValue: Int = 10
                    ): MyStepperState {
                        // We pass our custom Saver to rememberSaveable
                        return rememberSaveable(saver = MyStepperState.Saver) {
                            MyStepperState(
                                initialValue = initialValue,
                                minValue = minValue,
                                maxValue = maxValue
                            )
                        }
                    }
                    
                    @Preview(showBackground = true)
                    @Composable
                    private fun MyStepperPreview() {
                        val scope = rememberCoroutineScope()
                        val snackbarState = remember { SnackbarHostState() }

                        val state = rememberMyStepperState(
                            initialValue = 5,
                            minValue = 0,
                            maxValue = 20,
                        )

                        LaunchedEffect(state.value) {
                            if (!state.canIncrement) {
                                snackbarState.showSnackbar("You have reached the maximum value")
                            }

                            if (!state.canDecrement) {
                                snackbarState.showSnackbar("You have reached the minimum value")
                            }
                        }

                        MaterialTheme {
                            Scaffold(
                                snackbarHost = {
                                    SnackbarHost(hostState = snackbarState)
                                },
                            ) { contentPadding ->
                                Box(
                                    modifier = Modifier
                                        .fillMaxSize()
                                        .padding(contentPadding),
                                    contentAlignment = Alignment.Center,
                                ) {
                                    MyStepper(state = state)
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    data class User(val id: Int, val name: String)
                    
                    //1
                    val UserSaver = Saver<User, List<Any>>(
                        save = { user ->
                            listOf(user.id, user.name) // Convert to something saveable
                        },
                        restore = { list ->
                            User(
                                id = list[0] as Int,
                                name = list[1] as String
                            )
                        }
                    )
                    
                    val user = rememberSaveable(stateSaver = UserSaver) {
                        mutableStateOf(User(1, "Kamlesh"))
                    }
                    
                    //2
                    val UserSaver = listSaver<User, Any>(
                        save = { listOf(it.id, it.name) },
                        restore = { User(it[0] as Int, it[1] as String) }
                    )
                    
                    val user = rememberSaveable(stateSaver = UserSaver) {
                        mutableStateOf(User(1, "Kamlesh"))
                    }
                    """.trimIndent()
            )

            //https://github.com/binishmanandhar23/BreastCancerCare
            KotlinCode(
                code = """
                    import package name .LoaderState.Companion.Saver
                    
                    @Composable
                    fun CustomLoader(
                        modifier: Modifier = Modifier,
                        loaderState: LoaderState,
                        content: @Composable () -> Unit
                    ) {
                        val visible by loaderState.visible.collectAsStateWithLifecycle(false)

                        Box(modifier = modifier) {
                            MainContents(
                                content = content,
                                visible = visible,
                            )
                        }
                    }

                    @Composable
                    private fun MainContents(
                        modifier: Modifier = Modifier,
                        visible: Boolean,
                        content: @Composable () -> Unit
                    ) {
                        content()
                        AnimatedContent(
                            modifier = modifier.fillMaxWidth(),
                            targetState = visible,
                            transitionSpec = {
                                fadeIn() togetherWith  fadeOut()
                            },
                            label = "",
                        ) { show ->
                            if (show)
                                Box(
                                    modifier = Modifier
                                        .fillMaxSize()
                                        .background(color = MaterialTheme.colorScheme.background.copy(alpha = 0.4f))
                                ) {
                                    LinearProgressIndicator(
                                        modifier = Modifier.fillMaxWidth(),
                                        color = MaterialTheme.colorScheme.secondary,
                                        strokeCap = StrokeCap.Butt
                                    )
                                }
                        }
                    }

                    @Composable
                    fun rememberLoaderState(initialVisibility: Boolean = false): LoaderState =
                        rememberSaveable(saver = Saver) {
                            LoaderState(initialVisibility)
                        }
                        
                    class LoaderState(initialVisibility: Boolean) {
                        val visible = MutableStateFlow(initialVisibility)

                        fun show() {
                            visible.value = true
                        }

                        fun hide() {
                            visible.value = false
                        }

                        companion object {
                            /**
                             * The default [Saver] implementation for [LoaderState].
                             */
                            val Saver: Saver<LoaderState, *> = Saver(
                                save = { it.visible.value },
                                restore = { LoaderState(it) }
                            )
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/binishmanandhar23/BreastCancerCare
            KotlinCode(
                code = """
                    @Composable
                    fun CustomSnackBar(
                        modifier: Modifier = Modifier,
                        snackBarModifier: Modifier = Modifier,
                        text: String,
                        snackBarState: SnackBarState,
                        delay: Long? = null,
                        useBox: Boolean = false,
                        content: @Composable () -> Unit
                    ) {
                        val visible by snackBarState.visible.collectAsStateWithLifecycle(false)
                        val overridingText by snackBarState.overridingText.collectAsStateWithLifecycle(text)
                        val overridingDelay by snackBarState.overridingDelay.collectAsStateWithLifecycle(delay)
                        val innerText by remember (overridingText) { mutableStateOf(overridingText) }

                        overridingDelay?.let {
                            LaunchedEffect(key1 = visible) {
                                delay(it)
                                snackBarState.hide()
                            }
                        }

                        if (useBox)
                            Box(modifier = modifier) {
                                MainContents(
                                    content = content,
                                    visible = visible,
                                    innerText = innerText,
                                    modifier = snackBarModifier.align(
                                        Alignment.BottomCenter
                                    )
                                )
                            }
                        else
                            Column(modifier = modifier) {
                                MainContents(
                                    content = content,
                                    visible = visible,
                                    innerText = innerText,
                                    modifier = snackBarModifier.padding(top = 10.dp)
                                )
                            }

                    }

                    @OptIn(ExperimentalAnimationApi::class)
                    @Composable
                    private fun MainContents(
                        modifier: Modifier,
                        visible: Boolean,
                        innerText: String,
                        content: @Composable () -> Unit
                    ) {
                        content()
                        AnimatedContent(
                            modifier = modifier.fillMaxWidth(),
                            targetState = visible,
                            transitionSpec = {
                                slideIntoContainer(AnimatedContentTransitionScope.SlideDirection.Up) + fadeIn() with slideOutOfContainer(
                                    AnimatedContentTransitionScope.SlideDirection.Down
                                ) + fadeOut()
                            },
                            label = "",
                        ) { show ->
                            if (show)
                                Snackbar(
                                    modifier = modifier,
                                    containerColor = MaterialTheme.colorScheme.onBackground,
                                    shape = MaterialTheme.shapes.medium,
                                ) {
                                    Text(
                                        text = innerText
                                    )
                                }
                            else
                                Spacer(modifier = modifier.fillMaxWidth())
                        }
                    }

                    @Composable
                    fun rememberSnackBarState(initialVisibility: Boolean = false): SnackBarState =
                        rememberSaveable(saver = SnackBarState.Saver) {
                            SnackBarState(initialVisibility)
                        }

                    const val SnackBarLengthShort = 1500L
                    const val SnackBarLengthMedium = 2200L
                    const val SnackBarLengthLong = 3000L

                    class SnackBarState(initialVisibility: Boolean) {
                        val visible = MutableStateFlow(initialVisibility)
                        val overridingText = MutableStateFlow("")
                        val overridingDelay = MutableStateFlow<Long?>(null)

                        fun show(overridingText: String? = null, overridingDelay: Long? = null) {
                            visible.value = true
                            if (overridingText != null)
                                this.overridingText.value = overridingText

                            this.overridingDelay.value = overridingDelay
                        }

                        fun hide() {
                            visible.value = false
                        }

                        companion object {
                            /**
                             * The default [Saver] implementation for [SnackBarState].
                             */
                            val Saver: Saver<SnackBarState, *> = Saver(
                                save = { it.visible.value },
                                restore = { SnackBarState(it) }
                            )
                        }
                    }
                   
                    """.trimIndent()
            )

            //https://github.com/binishmanandhar23/BreastCancerCare
            KotlinCode(
                code = """
                    @Composable
                    fun FAQScreen(
                        loaderState: LoaderState,
                        snackBarState: SnackBarState,
                        viewModel: FAQViewModel = koinViewModel()
                    ) {
                        val uiState by viewModel.faqUIState.collectAsStateWithLifecycle()
                        var faqs: List<FaqDto>? by remember { mutableStateOf(null) }

                        LaunchedEffect(key1 = uiState) {
                            when (val state = uiState) {
                                is FAQUIState.Loading -> loaderState.show()
                                is FAQUIState.Success -> {
                                    faqs = state.data
                                    loaderState.hide()
                                }

                                else -> snackBarState.show(
                                    overridingText = state?.message,
                                    overridingDelay = SnackBarLengthMedium
                                ).also { loaderState.hide() }
                            }
                        }
                    }
                    
                    //Main Call
                    val loaderState = rememberLoaderState()
                    val customSnackBarState = rememberSnackBarState()
                    CustomLoader(loaderState = loaderState) {
                        CustomSnackBar(
                            text = "",
                            snackBarState = customSnackBarState,
                            useBox = true
                        ) { }
                    }    
                    """.trimIndent()
            )

            KotlinCode(
                code = $$"""
                    //Bundle using
                    
                    val UserSaver = Saver<User, List<Any>>(
                        save = { listOf(it.id, it.name) },
                        restore = { User(it[0] as Int, it[1] as String) }
                    )
                    
                    val LazyListStateSaver = Saver<LazyListState, Pair<Int, Int>>(
                        save = { state ->
                            state.firstVisibleItemIndex to state.firstVisibleItemScrollOffset
                        },
                        restore = { restored ->
                            LazyListState(
                                firstVisibleItemIndex = restored.first,
                                firstVisibleItemScrollOffset = restored.second
                            )
                        }
                    )
                    
                    @Composable
                    fun CustomSaverScreen(modifier: Modifier = Modifier) {
                        val listState = rememberSaveable(saver = LazyListStateSaver) {
                            LazyListState()
                        }

                        LazyColumn(
                            modifier = modifier,
                            state = listState
                        ) {
                            items(100) { index ->
                                Text(
                                    text = "Item $index",
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .padding(16.dp)
                                )
                            }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "LazyColumn Custom Saver",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When using a LazyColumn, you often want the user to return to the same scroll position after rotation or process death. By default, LazyListState cannot be saved with rememberSaveable — you need a custom saver.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            //https://proandroiddev.com/explained-custom-savers-in-remembersaveable-for-jetpack-compose-070211eb3e92
            KotlinCode(
                code = $$"""
                    val LazyListStateSaver = Saver<LazyListState, Pair<Int, Int>>(
                        save = { state ->
                            state.firstVisibleItemIndex to state.firstVisibleItemScrollOffset
                        },
                        restore = { restored ->
                            LazyListState(
                                firstVisibleItemIndex = restored.first,
                                firstVisibleItemScrollOffset = restored.second
                            )
                        }
                    )
                    
                    @Composable
                    fun CustomSaverScreen(modifier: Modifier = Modifier) {
                        val listState = rememberSaveable(saver = LazyListStateSaver) {
                            LazyListState()
                        }

                        LazyColumn(
                            modifier = modifier,
                            state = listState
                        ) {
                            items(100) { index ->
                                Text(
                                    text = "Item #$index",
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .padding(16.dp)
                                )
                            }
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/LinkoraApp/Linkora.git
            KotlinCode(
                code = """
                    @Composable
                    inline fun <reified T> rememberDeserializableMutableObject(noinline init: () -> MutableState<T>): MutableState<T> {
                        return rememberSaveable(saver = Saver(save = {
                            Json.encodeToString(it.value)
                        }, restore = {
                            mutableStateOf(Json.decodeFromString<T>(it))
                        }), init = init)
                    }
                    
                    //Use
                    val selectedFolderForSavingTheLink = rememberDeserializableMutableObject {
                        mutableStateOf(
                                
                        )
                    }
                    """.trimIndent()
            )
        }
    }
}